import {
    ApiClient,
    ApiShopperLandingPage,
    ApiShopperLandingPageLink,
    BlackCrowPageIds,
    TrackEvents,
    UX_TYPE,
    VLPVariant,
} from '@storefronts/common-types'
import { BlotoutTrackker } from '../components/pixels/BlotoutPixel'
import { ALL_PARAMS } from '../context/QueryParamContextProvider'
import {
    Cart,
    CollectionPlayerVideo,
    PlaylistPlayerVideo,
    VariantWithImage,
} from '../types'
import {
    EL_ADD_TO_CART,
    EL_REMOVE_FROM_CART,
    EL_SELECT_ITEM,
    EL_VIEW_CART,
} from './elevar'
import { BlackCrowLogger } from './blackcrow'
import {
    AXON_ADD_TO_CART,
    AXON_BEGIN_CHECKOUT,
    AXON_BEGIN_CHECKOUT_BUY_NOW,
    AXON_PAGE_VIEW,
    AXON_REMOVE_FROM_CART,
    AXON_VIEW_CART,
    AXON_VIEW_ITEM,
} from './applovin'
import { GoogleAdsTracker } from './googleAds'
import { MetaPixelTracker } from './meta'
import { v4 as uuidv4 } from 'uuid'

type EventQueueObj = {
    event: CustomEvent
    timestamp: number
}

declare global {
    interface Window {
        viddyDataLayer: EventQueueObj[]
        blackcrow: any
        viddyPIPMode: string
        viddyAnalyticsLoaded: boolean
        viddyTrackers: Record<string, AnalyticsTracker>
        convert?: any
        optimizely?: any
        google_optimize?: any
        fbq?: any
        gtag?: any
        dataLayer?: unknown[]
        isAramore?: boolean
        attentive?: any
        postscript?: any
        _klOnsite?: any
        klaviyo?: any
        edgetag?: any
        TriplePixel?: any
        cvg?: any
        __sfMarkVendorReady?: (vendor: string) => void
    }
}

const logViddyEvent = (e: CustomEvent) => {
    if (!window.viddyDataLayer) {
        window.viddyDataLayer = []
    }
    window.viddyDataLayer.push({ event: e, timestamp: Date.now() })
}

type AnalyticsInitializeParams = {
    landingPage: ApiShopperLandingPage
    format: 'vlp'
}

const getGlobalDetails = () => {
    return {
        path: window.location.pathname,
        host: window.location.host,
        url: window.location.href,
    }
}
const getClientDetails = (client: ApiClient) => {
    return {
        clientHash: client.clientHash,
        clientName: client.name,
    }
}

const getLandingPageDetails = (landingPage: ApiShopperLandingPage) => {
    return {
        landingPageHash: landingPage.landingPageHash,
        landingPageName: landingPage.name,
    }
}

const getVideoDetails = (
    video: PlaylistPlayerVideo | CollectionPlayerVideo,
) => {
    return {
        videoId: video.videoHash,
        videoName: video.name,
        videoIndex: video.videoIndex,
        videoDuration: video.duration,
        playlistHash: video.playlistHash,
    }
}

export type BrandChatMessageInput = 'typed' | 'suggestion'

type CTAClickInput =
    | {
          type: 'video_primary'
          videoName: string
          productTitle: string
      }
    | {
          type: 'video_secondary'
          videoName: string
          productTitle: string
      }
    | {
          type: 'pdp_primary'
          productTitle: string
      }
    | {
          type: 'plp_primary'
          productTitle: string
      }
    | {
          type: 'email_vlp_primary'
          location:
              | 'overlay'
              | 'footer'
              | 'pdp'
              | 'full_video'
              | 'main_video'
              | 'inline'
      }
    | {
          type: 'email_vlp_buy_now'
          location: 'pdp'
      }
    | {
          type: 'email_vlp_secondary'
          location: 'overlay'
      }
    | {
          type: 'cart_upsell'
      }

export class AnalyticsTracker {
    private commonDetails: Record<string, any> = {}
    private clientHash: string
    private googleAdsTagId: string | null | undefined
    private axonScrollViewItemVariantIds: Set<string> = new Set()

    public constructor() {
        this.clientHash = ''
        this.googleAdsTagId = null
    }

    public initialize(initParams: AnalyticsInitializeParams) {
        this.clientHash = initParams.landingPage.client.clientHash
        this.googleAdsTagId =
            initParams.landingPage.integrations?.googleAdsTagId
        this.axonScrollViewItemVariantIds = new Set()

        let details = {
            ...getGlobalDetails(),
            format: initParams.format,
        }
        if (initParams.format === 'vlp') {
            details = {
                ...details,
                ...getClientDetails(initParams.landingPage.client),
                ...getLandingPageDetails(initParams.landingPage),
            }
        }

        const allParams = ALL_PARAMS()
        const keys = Object.keys(allParams)
        const copy: Record<string, string> = {}
        keys.forEach((key) => {
            const keyCopy = 'qp_' + key.replace(/-/g, '_')
            copy[keyCopy] = allParams[key]
        })
        details = {
            ...details,
            ...copy,
        }

        this.commonDetails = details
    }

    private getPayloadWithCommonData(payload: Record<string, any>) {
        return {
            detail: {
                ...payload,
                ...this.commonDetails,
            },
        }
    }

    // Every later event in the session carries brandChatOpened, so cart and
    // checkout events can be split by whether the shopper used the chat.
    TRACK_BRAND_CHAT_OPEN = () => {
        this.commonDetails = { ...this.commonDetails, brandChatOpened: true }
        const event = new CustomEvent(
            TrackEvents.BRAND_CHAT_OPEN,
            this.getPayloadWithCommonData({}),
        )
        logViddyEvent(event)
    }

    TRACK_BRAND_CHAT_MESSAGE_SEND = (input: BrandChatMessageInput) => {
        const event = new CustomEvent(
            TrackEvents.BRAND_CHAT_MESSAGE_SEND,
            this.getPayloadWithCommonData({ input }),
        )
        logViddyEvent(event)
    }

    TRACK_BRAND_CHAT_PRODUCT_CLICK = (
        product: ApiShopperLandingPageLink,
        placement: 'hero_card' | 'product_card' | 'comparison',
    ) => {
        const event = new CustomEvent(
            TrackEvents.BRAND_CHAT_PRODUCT_CLICK,
            this.getPayloadWithCommonData({
                placement,
                productTitle: product.title,
                shopifyId: product.shopifyId,
            }),
        )
        logViddyEvent(event)
    }

    TRACK_CAROUSEL_EXPAND = (
        playlist: { playlistHash: string; name: string },
        videoName: string,
        videoHash: string,
    ) => {
        const eventName = TrackEvents.CAROUSEL_EXPAND
        const event = new CustomEvent(
            eventName,
            this.getPayloadWithCommonData({
                playlistHash: playlist.playlistHash,
                playlistName: playlist.name,
                videoName,
                videoHash,
            }),
        )
        logViddyEvent(event)
    }

    // SHARED
    TRACK_VIDEO_IMPRESSION = (video: PlaylistPlayerVideo) => {
        const eventName = TrackEvents.VIDEO_IMPRESSION
        const details = this.getPayloadWithCommonData(getVideoDetails(video))
        const event = new CustomEvent(eventName, details)
        logViddyEvent(event)
    }

    TRACK_VIDEO_SECONDS = (
        video: PlaylistPlayerVideo,
        seconds: 3 | 6 | 10 | 30,
    ) => {
        let eventName: string | null = null
        switch (seconds) {
            case 3:
                eventName = TrackEvents.VIDEO_3_SECONDS
                break
            case 6:
                eventName = TrackEvents.VIDEO_6_SECONDS
                break
            case 10:
                eventName = TrackEvents.VIDEO_10_SECONDS
                break
            case 30:
                eventName = TrackEvents.VIDEO_30_SECONDS
                break
        }
        const details = this.getPayloadWithCommonData(getVideoDetails(video))
        const event = new CustomEvent(eventName, details)
        logViddyEvent(event)
    }

    TRACK_VIDEO_FIRST_QUARTILE = (video: PlaylistPlayerVideo) => {
        const eventName = TrackEvents.VIDEO_FIRST_QUARTILE
        const details = this.getPayloadWithCommonData(getVideoDetails(video))
        const event = new CustomEvent(eventName, details)
        logViddyEvent(event)
    }

    TRACK_VIDEO_MIDPOINT = (video: PlaylistPlayerVideo) => {
        const eventName = TrackEvents.VIDEO_MIDPOINT
        const details = this.getPayloadWithCommonData(getVideoDetails(video))
        const event = new CustomEvent(eventName, details)
        logViddyEvent(event)
    }

    TRACK_VIDEO_THIRD_QUARTILE = (video: PlaylistPlayerVideo) => {
        const eventName = TrackEvents.VIDEO_THIRD_QUARTILE
        const details = this.getPayloadWithCommonData(getVideoDetails(video))
        const event = new CustomEvent(eventName, details)
        logViddyEvent(event)
    }

    TRACK_VIDEO_COMPLETE = (video: PlaylistPlayerVideo) => {
        const eventName = TrackEvents.VIDEO_COMPLETE
        const details = this.getPayloadWithCommonData(getVideoDetails(video))
        const event = new CustomEvent(eventName, details)
        logViddyEvent(event)
    }

    TRACK_VIDEO_PLAYER_PLAY_TOGGLE = (action: 'pause' | 'play') => {
        const eventName =
            action === 'pause'
                ? TrackEvents.VIDEO_PLAYER_PAUSE_BUTTON_CLICKED
                : TrackEvents.VIDEO_PLAYER_PLAY_BUTTON_CLICKED
        const details = this.getPayloadWithCommonData({})
        const event = new CustomEvent(eventName, details)
        logViddyEvent(event)
    }

    TRACK_VIDEO_PLAYER_MUTE_TOGGLE = (
        muted: 'muted' | 'unmuted',
        location:
            | 'video_tap'
            | 'full_screen_icon_click'
            | 'main_video_icon_click'
            | 'badge_click',
    ) => {
        const eventName =
            muted === 'muted'
                ? TrackEvents.VIDEO_PLAYER_MUTE
                : TrackEvents.VIDEO_PLAYER_UNMUTE
        const details = this.getPayloadWithCommonData({
            location,
        })
        const event = new CustomEvent(eventName, details)
        logViddyEvent(event)
    }

    // LANDING PAGE
    TRACK_LANDING_PAGE_IMPRESSION = (variant: UX_TYPE, viddyId: string) => {
        const eventName = TrackEvents.LANDING_PAGE_IMPRESSION
        this.commonDetails.variant = variant
        this.commonDetails = {
            ...this.commonDetails,
            viddyId,
        }
        const event = new CustomEvent(
            eventName,
            this.getPayloadWithCommonData({ variant }),
        )
        BlotoutTrackker.trackPageView()
        BlackCrowLogger.logPageViewEvent(BlackCrowPageIds.landingPage)
        AXON_PAGE_VIEW()
        GoogleAdsTracker.trackPageView(this.googleAdsTagId)
        logViddyEvent(event)
    }

    TRACK_LANDING_PAGE_VIDEO_CONTAINER_CLICK = () => {
        const eventName = TrackEvents.LANDING_PAGE_VIDEO_CONTAINER_CLICK
        const event = new CustomEvent(
            eventName,
            this.getPayloadWithCommonData({}),
        )
        logViddyEvent(event)
    }

    TRACK_PRODUCT_FILTER_BUTTON_CLICK = () => {
        const eventName = TrackEvents.LANDING_PAGE_PRODUCT_FILTER_BUTTON_CLICK
        const event = new CustomEvent(
            eventName,
            this.getPayloadWithCommonData({}),
        )
        logViddyEvent(event)
    }

    TRACK_PRODUCT_FILTER_SHOW_RESULTS_CLICK = () => {
        const eventName =
            TrackEvents.LANDING_PAGE_PRODUCT_FILTER_SHOW_RESULTS_CLICK
        const event = new CustomEvent(
            eventName,
            this.getPayloadWithCommonData({}),
        )
        logViddyEvent(event)
    }

    TRACK_PRODUCT_FILTER_CLEAR_FILTERS_CLICK = () => {
        const eventName =
            TrackEvents.LANDING_PAGE_PRODUCT_FILTER_CLEAR_FILTERS_CLICK
        const event = new CustomEvent(
            eventName,
            this.getPayloadWithCommonData({}),
        )
        logViddyEvent(event)
    }

    TRACK_LANDING_PAGE_N_SECOND_LOAD = (n: 1 | 3 | 5) => {
        const eventName = `vd_landing_page_${n}_second_load`
        const event = new CustomEvent(
            eventName,
            this.getPayloadWithCommonData({}),
        )
        logViddyEvent(event)
    }

    TRACK_LANDING_PAGE_CTA_CLICK = (input: CTAClickInput) => {
        const eventName = 'vd_landing_page_cta_click'
        const event = new CustomEvent(
            eventName,
            this.getPayloadWithCommonData({ ...input }),
        )
        logViddyEvent(event)
    }

    TRACK_LANDING_PAGE_MENU_ITEM_CLICK = (
        product: ApiShopperLandingPageLink,
    ) => {
        const eventName = TrackEvents.LANDING_PAGE_MENU_ITEM_CLICK
        const extraData = {
            productTitle: product.title,
        }
        const event = new CustomEvent(eventName, {
            detail: {
                ...this.getPayloadWithCommonData({}).detail,
                ...extraData,
            },
        })

        EL_SELECT_ITEM(product)
        logViddyEvent(event)
    }

    TRACK_PRODUCT_SCROLL_VIEW_CONTENT = (
        product: ApiShopperLandingPageLink,
        variant?: VLPVariant | null,
    ) => {
        const eventName = 'vd_landing_page_single_product_scroll'
        const event = new CustomEvent(eventName, {
            detail: {
                ...this.getPayloadWithCommonData({
                    productTitle: product.title,
                }).detail,
            },
        })

        logViddyEvent(event)
        MetaPixelTracker.trackViewContentFromLink(this.clientHash, product)

        const variantId = variant?.shopifyId
        if (variantId && !this.axonScrollViewItemVariantIds.has(variantId)) {
            this.axonScrollViewItemVariantIds.add(variantId)
            AXON_VIEW_ITEM(product, undefined, variant)
        }
    }

    TRACK_LANDING_PAGE_PRODUCT_CARD_CLICK = (
        videoName: string,
        product: ApiShopperLandingPageLink,
    ) => {
        const eventName = TrackEvents.LANDING_PAGE_PRODUCT_CARD_CLICK
        const event = new CustomEvent(eventName, {
            detail: {
                ...this.getPayloadWithCommonData({
                    videoName,
                    productTitle: product.title,
                }).detail,
            },
        })
        logViddyEvent(event)
    }

    // NEW EVENTS
    TRACK_CART_PAGEVIEW = (cart: Cart) => {
        const eventName = TrackEvents.LANDING_PAGE_CART_PAGEVIEW
        const event = new CustomEvent(eventName, {
            detail: {
                ...this.getPayloadWithCommonData({}).detail,
            },
        })

        EL_VIEW_CART(cart)
        BlackCrowLogger.logPageViewEvent(BlackCrowPageIds.cartPage)
        GoogleAdsTracker.trackPageView(this.googleAdsTagId)
        AXON_VIEW_CART(cart)
        logViddyEvent(event)
        MetaPixelTracker.trackViewContentCartPage(this.clientHash, cart)
    }

    TRACK_PDP_PAGEVIEW = (product: ApiShopperLandingPageLink) => {
        const eventName = TrackEvents.LANDING_PAGE_PDP_PAGEVIEW
        const event = new CustomEvent(eventName, {
            detail: {
                ...this.getPayloadWithCommonData({}).detail,
            },
        })
        logViddyEvent(event)

        BlackCrowLogger.logPageViewEvent(BlackCrowPageIds.pdpPage)
        GoogleAdsTracker.trackPageView(this.googleAdsTagId)

        GoogleAdsTracker.trackViewItem(this.googleAdsTagId, product)
        MetaPixelTracker.trackViewContentFromLink(this.clientHash, product)
    }

    // Linked BlackCrow ViewedProduct + Axon view_item share a dedupe id so
    // downstream reconciliation works. Nullable variant keeps the pair firing
    // for products without variantInfo (AXON_VIEW_ITEM falls back to link data).
    TRACK_PDP_VARIANT_VIEW = (
        product: ApiShopperLandingPageLink,
        variant: VLPVariant | null,
    ) => {
        const eventId = uuidv4()

        AXON_VIEW_ITEM(product, eventId, variant)

        BlackCrowLogger.triggerStorefrontsAnalyticsEvent(
            'ViewedProduct',
            eventId,
            { product },
            'storefronts_v2',
        )
    }

    TRACK_PLP_PAGEVIEW = () => {
        const eventName = TrackEvents.LANDING_PAGE_PLP_PAGEVIEW
        const event = new CustomEvent(eventName, {
            detail: {
                ...this.getPayloadWithCommonData({}).detail,
            },
        })
        BlackCrowLogger.logPageViewEvent(BlackCrowPageIds.plpPage)
        GoogleAdsTracker.trackPageView(this.googleAdsTagId)
        logViddyEvent(event)
        MetaPixelTracker.trackViewContentProductListing(this.clientHash)
    }

    TRACK_PDP_IMAGE_CAROUSEL_SWIPE = (productTitle: string) => {
        const eventName = TrackEvents.LANDING_PAGE_PDP_IMAGE_CAROUSEL_SWIPE
        const event = new CustomEvent(eventName, {
            detail: {
                ...this.getPayloadWithCommonData({ productTitle }).detail,
            },
        })
        logViddyEvent(event)
    }

    TRACK_STATIC_CAROUSEL_SWIPE = (title?: string) => {
        const eventName = TrackEvents.LANDING_PAGE_STATIC_CAROUSEL_SWIPE
        const event = new CustomEvent(eventName, {
            detail: {
                ...this.getPayloadWithCommonData({}).detail,
                title,
            },
        })
        logViddyEvent(event)
    }

    // CART STUFF

    TRACK_CART_ADD = (product: VariantWithImage, quantity: number) => {
        const eventName = TrackEvents.LANDING_PAGE_CART_QUANTITY_UPDATE
        const event = new CustomEvent(eventName, {
            detail: {
                ...this.getPayloadWithCommonData({
                    productTitle: product.title,
                    quantity,
                }).detail,
            },
        })

        EL_ADD_TO_CART(product, quantity)
        AXON_ADD_TO_CART(product, quantity)
        MetaPixelTracker.trackAddToCart(this.clientHash, product, quantity)
        GoogleAdsTracker.trackAddToCart(this.googleAdsTagId, product, quantity)
        BlackCrowLogger.logPageViewEvent(BlackCrowPageIds.addToCartPage)
        logViddyEvent(event)
    }

    TRACK_CART_QUANTITY_DELTA = (
        variant: VariantWithImage,
        previousQuantity: number,
        newQuantity: number,
    ) => {
        const delta = newQuantity - previousQuantity
        if (delta > 0) {
            this.TRACK_CART_ADD(variant, delta)
        } else if (delta < 0) {
            this.TRACK_CART_REMOVE_ITEM(variant, Math.abs(delta))
        }
    }

    TRACK_CART_REMOVE_ITEM = (variant: VariantWithImage, quantity: number) => {
        const eventName = TrackEvents.LANDING_PAGE_CART_REMOVE_ITEM
        const event = new CustomEvent(eventName, {
            detail: {
                ...this.getPayloadWithCommonData({
                    productTitle: variant.title,
                }).detail,
            },
        })
        EL_REMOVE_FROM_CART(variant)
        AXON_REMOVE_FROM_CART(variant, quantity)
        logViddyEvent(event)
    }

    TRACK_HEADER_LOGO_CLICK = (data: Record<string, string>) => {
        const eventName = TrackEvents.LANDING_PAGE_HEADER_CLICK
        const event = new CustomEvent(eventName, {
            detail: {
                ...this.getPayloadWithCommonData(data).detail,
            },
        })
        logViddyEvent(event)
    }

    TRACK_CART_CHECKOUT_CLICK = (options?: {
        cart?: Cart
        buyNowVariant?: VariantWithImage
    }) => {
        const eventName = TrackEvents.LANDING_PAGE_CART_CHECKOUT_CLICK
        const event = new CustomEvent(eventName, {
            detail: {
                ...this.getPayloadWithCommonData({ type: 'card' }).detail,
            },
        })

        BlackCrowLogger.logPageViewEvent(BlackCrowPageIds.initiateCheckoutPage)
        if (options?.cart) {
            AXON_BEGIN_CHECKOUT(options.cart)
        } else if (options?.buyNowVariant) {
            AXON_BEGIN_CHECKOUT_BUY_NOW(options.buyNowVariant)
        }
        logViddyEvent(event)
        MetaPixelTracker.trackInitiateCheckout(this.clientHash, {
            cart: options?.cart,
            buyNowVariant: options?.buyNowVariant,
        })
        GoogleAdsTracker.trackBeginCheckout(this.googleAdsTagId, {
            cart: options?.cart,
            buyNowVariant: options?.buyNowVariant,
        })
    }

    TRACK_TAB_BAR_CLICK = (tabTitle: string) => {
        const eventName = TrackEvents.LANDING_PAGE_TAB_BAR_CLICK
        const event = new CustomEvent(eventName, {
            detail: {
                ...this.getPayloadWithCommonData({ tabTitle }).detail,
            },
        })
        logViddyEvent(event)
    }

    // REVIEWS
    TRACK_REVIEWS_SUMMARY_CLICK = (location: 'content-block' | 'pdp') => {
        const eventName = TrackEvents.LANDING_PAGE_REVIEWS_SUMMARY_CLICK
        const event = new CustomEvent(eventName, {
            detail: {
                ...this.getPayloadWithCommonData({ location }).detail,
            },
        })
        logViddyEvent(event)
    }

    TRACK_SEE_MORE_REVIEWS_CLICK = (location: 'content-block' | 'pdp') => {
        const eventName = TrackEvents.LANDING_PAGE_SEE_MORE_REVIEWS_CLICK
        const event = new CustomEvent(eventName, {
            detail: {
                ...this.getPayloadWithCommonData({ location }).detail,
            },
        })
        logViddyEvent(event)
    }

    TRACK_PDP_VIEW_ON_SITE_CLICK = (productName: string, shopifyId: string) => {
        const eventName = TrackEvents.LANDING_PAGE_PDP_VIEW_ON_SITE_CLICK
        const event = new CustomEvent(eventName, {
            detail: {
                ...this.getPayloadWithCommonData({
                    productName,
                    shopifyId,
                }).detail,
            },
        })
        logViddyEvent(event)
    }

    TRACK_PLP_VIEW_ON_SITE_CLICK = (clientName: string) => {
        const eventName = TrackEvents.LANDING_PAGE_PLP_VIEW_ON_SITE_CLICK
        const event = new CustomEvent(eventName, {
            detail: {
                ...this.getPayloadWithCommonData({
                    clientName,
                }).detail,
            },
        })
        logViddyEvent(event)
    }

    TRACK_VARIANT_SELECTION = (
        productTitle: string,
        optionName: string,
        optionValue: string,
        isAvailable: boolean,
    ) => {
        const eventName = 'vd_variant_selection'
        const event = new CustomEvent(eventName, {
            detail: {
                ...this.getPayloadWithCommonData({
                    productTitle,
                    optionName,
                    optionValue,
                    isAvailable,
                }).detail,
            },
        })
        logViddyEvent(event)
    }
}
